import React from 'react'
import { styled } from '@mui/material/styles'

import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'

import { cardSx } from 'src/utils/views'
import { formatNumber } from 'src/utils/formatter-utils'

const TileContainer = styled(Paper)({
    ...cardSx,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '13px 12px',
    minWidth: 0,
    flexGrow: 1,
    overflow: 'hidden'
})

const TileValueRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
    minWidth: 0,
    marginBottom: 6
})

const TileValue = styled('span')({
    fontSize: 'clamp(20px, 1.6vw, 26px)',
    lineHeight: 1,
    fontWeight: 800,
    color: 'var(--ink)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
})

/** Denominador do total, quando a métrica é uma fração ("1.204 / 1.500"). */
const TileTotal = styled('span')({
    fontSize: 14,
    fontWeight: 650,
    lineHeight: 1,
    color: 'var(--muted)',
    whiteSpace: 'nowrap',
    flexShrink: 0
})

const TileLabel = styled('span')({
    fontSize: 11.5,
    color: 'var(--muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
})

const TileBar = styled(LinearProgress)({
    height: 4,
    borderRadius: 2,
    marginTop: 6,
    backgroundColor: 'var(--chart-track)',
    '& .MuiLinearProgress-bar': { backgroundColor: 'var(--chart-emphasis)' }
})

export type AccessKpiTileProps = {
    label: string
    value: number
    total?: number
    className?: string
}

export function AccessKpiTile({ label, value, total, className }: AccessKpiTileProps) {
    const hasTotal = total != null
    const percent = hasTotal && total > 0 ? Math.min(100, (value / total) * 100) : 0

    const title = hasTotal
        ? `${formatNumber(value)} de ${formatNumber(total)} (${percent.toFixed(1).replace('.', ',')}%)`
        : formatNumber(value)

    return (
        <TileContainer className={className} elevation={0}>
            <TileValueRow title={title}>
                <TileValue>{formatNumber(value)}</TileValue>
                {hasTotal && <TileTotal>{`/ ${formatNumber(total)}`}</TileTotal>}
            </TileValueRow>
            <TileLabel title={label}>{label}</TileLabel>
            {hasTotal && <TileBar variant="determinate" value={percent} />}
        </TileContainer>
    )
}

export default AccessKpiTile
